import { EventEmitter } from 'node:events';

interface CdpTarget {
  webSocketDebuggerUrl?: string;
}

/** Raw CDP connection returned by chrome-remote-interface. */
interface RawCdpConnection {
  Page: {
    captureScreenshot(opts: { format: string }): Promise<{ data: string }>;
    startScreencast(opts: {
      format: string;
      quality: number;
      maxWidth?: number;
      maxHeight?: number;
      everyNthFrame?: number;
    }): Promise<void>;
    stopScreencast(): Promise<void>;
    screencastFrameAck(opts: { sessionId: number }): Promise<void>;
  };
  Runtime: {
    evaluate(opts: {
      expression: string;
      returnByValue: boolean;
    }): Promise<{ result: { value: unknown } }>;
  };
  on(event: string, handler: (...args: unknown[]) => void): void;
  close(): Promise<void>;
}

export interface PageInfo {
  url: string;
  title: string;
}

/** Interface for CDP operations — used by RecordingEngine for testability. */
export interface ICdpClient {
  connect(): Promise<void>;
  disconnect(): Promise<void>;
  captureScreenshot(): Promise<Buffer | null>;
  getPageInfo(): Promise<PageInfo | null>;
  startScreencast(quality: number, maxFps: number): Promise<void>;
  stopScreencast(): Promise<void>;
  on(event: 'screencast-frame', listener: (data: Buffer) => void): this;
  on(event: string, listener: (...args: unknown[]) => void): this;
}

/**
 * Thin CDP client for capturing screenshots and page info.
 * Connects to Chrome via the /json endpoint, then opens a CDP session.
 * Best-effort: if connection fails, methods return gracefully (no crash).
 */
export class CdpClient extends EventEmitter implements ICdpClient {
  private client: RawCdpConnection | null = null;
  private readonly host: string;
  private readonly port: number;
  private connecting = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private backoffMs = 1000;
  private readonly _maxBackoffMs = 30_000;

  constructor(host: string, port: number) {
    super();
    this.host = host;
    this.port = port;
  }

  async connect(): Promise<void> {
    if (this.client || this.connecting) return;
    this.connecting = true;

    try {
      const wsUrl = await this.discoverWsUrl();
      if (!wsUrl) {
        this.connecting = false;
        return;
      }

      const CDP = (await import('chrome-remote-interface')).default;
      this.client = (await CDP({ target: wsUrl })) as unknown as RawCdpConnection;
      this.backoffMs = 500;
      this.connecting = false;

      this.client.on('disconnect', () => {
        this.client = null;
        this.scheduleReconnect();
      });
    } catch (err) {
      this.connecting = false;
      console.error(
        `[browserforge] CDP connection failed: ${err instanceof Error ? err.message : String(err)}`,
      );
      this.scheduleReconnect();
    }
  }

  async disconnect(): Promise<void> {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    await this.stopScreencast();
    if (this.client) {
      try {
        await this.client.close();
      } catch {
        // Ignore close errors
      }
      this.client = null;
    }
  }

  async captureScreenshot(): Promise<Buffer | null> {
    if (!this.client) return null;
    try {
      const result = await this.client.Page.captureScreenshot({
        format: 'png',
      });
      return Buffer.from(result.data, 'base64');
    } catch (err) {
      console.error(
        `[browserforge] Screenshot failed: ${err instanceof Error ? err.message : String(err)}`,
      );
      return null;
    }
  }

  async getPageInfo(): Promise<PageInfo | null> {
    if (!this.client) return null;
    try {
      const urlResult = await this.client.Runtime.evaluate({
        expression: 'document.URL',
        returnByValue: true,
      });
      const titleResult = await this.client.Runtime.evaluate({
        expression: 'document.title',
        returnByValue: true,
      });
      return {
        url: String(urlResult.result.value),
        title: String(titleResult.result.value),
      };
    } catch (err) {
      console.error(
        `[browserforge] Page info failed: ${err instanceof Error ? err.message : String(err)}`,
      );
      return null;
    }
  }

  private screencastActive = false;
  private screencastHandler: ((params: { data: string; sessionId: number }) => void) | null = null;

  async startScreencast(quality: number, maxFps: number): Promise<void> {
    if (!this.client || this.screencastActive) return;

    this.screencastHandler = (params) => {
      // Ack the frame immediately so CDP keeps sending
      void this.client?.Page.screencastFrameAck({ sessionId: params.sessionId });
      // Emit the JPEG data as a Buffer
      const buf = Buffer.from(params.data, 'base64');
      this.emit('screencast-frame', buf);
    };

    this.client.on('Page.screencastFrame', this.screencastHandler as (...args: unknown[]) => void);

    try {
      await this.client.Page.startScreencast({
        format: 'jpeg',
        quality,
        maxWidth: 1280,
        maxHeight: 720,
        everyNthFrame: Math.max(1, Math.round(60 / maxFps)),
      });
      this.screencastActive = true;
    } catch (err) {
      console.error(
        `[browserforge] Screencast start failed: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  }

  async stopScreencast(): Promise<void> {
    if (!this.screencastActive) return;
    this.screencastActive = false;

    if (this.client) {
      try {
        await this.client.Page.stopScreencast();
      } catch {
        // Ignore stop errors (connection may be gone)
      }
    }
  }

  get isScreencasting(): boolean {
    return this.screencastActive;
  }

  get isConnected(): boolean {
    return this.client !== null;
  }

  get reconnectBackoffMs(): number {
    return this.backoffMs;
  }

  get maxReconnectBackoffMs(): number {
    return this._maxBackoffMs;
  }

  private async discoverWsUrl(): Promise<string | null> {
    // Prefer page targets (/json) — they expose Page domain (captureScreenshot).
    // The browser-level WS from /json/version does NOT have Page domain.
    try {
      const res = await fetch(`http://${this.host}:${this.port}/json`);
      if (res.ok) {
        const targets = (await res.json()) as CdpTarget[];
        const page = targets.find(
          (t) => (t as Record<string, unknown>)['type'] === 'page',
        );
        if (page?.webSocketDebuggerUrl) return page.webSocketDebuggerUrl;
      }
    } catch { /* fall through to /json/version */ }

    // Fallback: browser-level WS (limited CDP domains)
    try {
      const res = await fetch(
        `http://${this.host}:${this.port}/json/version`,
      );
      if (!res.ok) return null;
      const data = (await res.json()) as CdpTarget;
      return data.webSocketDebuggerUrl ?? null;
    } catch {
      return null;
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect();
    }, this.backoffMs);
    this.backoffMs = Math.min(this.backoffMs * 2, this._maxBackoffMs);
  }
}
